import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Megaphone, CheckCircle2, AlertTriangle, Link2, Loader2, RefreshCw,
  ArrowLeft, FileText, LayoutGrid,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Separator } from './ui/separator';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';
import { createSignupFlow } from '../utils/whatsappSignupCoordinator';

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso; }
};

// Map a backend error to a friendly, actionable message key (no raw Graph).
const errorKey = (e) => {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  const code = typeof detail === 'object' ? detail?.code : detail;
  if (status === 503) return 'notConfigured';
  if (code === 'REAUTH_REQUIRED') return 'reauthRequired';
  if (code === 'NOT_CONNECTED') return 'errorState';
  if (code === 'META_UNAVAILABLE') return 'metaUnavailable';
  if (code === 'page_not_accessible') return 'pageNotAccessible';
  if (code === 'form_not_accessible') return 'formNotAccessible';
  if (code === 'no_forms_selected') return 'selectAtLeastOneForm';
  return 'startFailed';
};

// P1 — WhatsApp Embedded Signup (v4). Launches Meta's flow, captures the
// authorization CODE + asset ids, and completes server-side. No tokens ever
// touch the browser.
const WhatsAppSignupSection = ({ conn, onChanged, hideSeparator = false }) => {
  const { t } = useTranslation();
  const kc = 'provider.metaConnect.whatsapp';
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pin, setPin] = useState('');
  const [pinStep, setPinStep] = useState(null);   // { phoneNumberId } when /register is required
  const [sdkReady, setSdkReady] = useState(typeof window !== 'undefined' && !!window.FB);
  const [sdkError, setSdkError] = useState(false);
  const flowRef = useRef(null);
  const sdkPromiseRef = useRef(null);
  const wa = conn?.whatsapp || {};
  const activeAssets = (wa.phone_assets || []).filter((a) => a.status === 'active');
  const provisioningAssets = (wa.phone_assets || []).filter(
    (a) => a.status === 'provisioning' || a.status === 'pending_registration');
  const isConnected = !!wa.enabled && activeAssets.length > 0;

  useEffect(() => {
    api.get('/meta/whatsapp/config').then(({ data }) => setCfg(data?.config || null)).catch(() => setCfg(null));
  }, []);

  // Deterministic cleanup if the component unmounts mid-flow.
  useEffect(() => () => { flowRef.current?.dispose?.(); }, []);

  // Load (and FB.init) the Meta JS SDK exactly once. Returns a cached promise
  // so preload + any click share the same initialization. The SDK persists on
  // `window.FB` across mounts, so once ready it stays ready.
  const loadSdk = useCallback((appId, version) => {
    if (window.FB) return Promise.resolve(window.FB);
    if (sdkPromiseRef.current) return sdkPromiseRef.current;
    sdkPromiseRef.current = new Promise((resolve, reject) => {
      const prevInit = window.fbAsyncInit;
      window.fbAsyncInit = () => {
        try { prevInit && prevInit(); } catch { /* ignore other consumers */ }
        window.FB.init({ appId, cookie: true, xfbml: false, version });
        resolve(window.FB);
      };
      let s = document.getElementById('facebook-jssdk');
      if (!s) {
        s = document.createElement('script');
        s.id = 'facebook-jssdk';
        s.src = 'https://connect.facebook.net/en_US/sdk.js';
        s.async = true; s.defer = true; s.crossOrigin = 'anonymous';
        s.onerror = () => { sdkPromiseRef.current = null; reject(new Error('sdk_load_failed')); };
        document.body.appendChild(s);
      }
    });
    return sdkPromiseRef.current;
  }, []);

  // FIRST-CLICK FIX: preload + initialize the SDK as soon as the config is
  // known, BEFORE the user clicks. This guarantees `window.FB` exists by click
  // time so `FB.login` runs synchronously inside the click gesture and the
  // Embedded Signup popup is not suppressed by the browser. (Previously the
  // SDK was loaded lazily inside the click handler, so the very first click
  // invoked FB.login after an async script download — outside user activation
  // — and Meta never opened until a remount left window.FB already present.)
  useEffect(() => {
    if (!cfg?.configured || !cfg.app_id) return undefined;
    let cancelled = false;
    loadSdk(cfg.app_id, cfg.graph_version || 'v26.0')
      .then(() => { if (!cancelled) { setSdkReady(true); setSdkError(false); } })
      .catch(() => { if (!cancelled) { setSdkReady(false); setSdkError(true); } });
    return () => { cancelled = true; };
  }, [cfg, loadSdk]);

  const complete = useCallback(async (code, phoneNumberId, wabaId, event) => {
    setBusy(true);
    try {
      const { data } = await api.post('/meta/whatsapp/embedded-signup/complete', {
        code, phone_number_id: phoneNumberId, waba_id: wabaId, event,
      });
      if (data?.pin_required) {
        setPinStep({ phoneNumberId: data.phone_number_id });   // ask for PIN ONLY now
        toast({ title: t(`${kc}.pinNeeded`) });
      } else if (data?.finalize === 'action_required') {
        toast({ title: t(`${kc}.actionRequired`), variant: 'destructive' });
        onChanged?.(data?.connection);
      } else {
        toast({ title: t(`${kc}.connectedToast`) });
        onChanged?.(data?.connection);
      }
    } catch {
      toast({ title: t(`${kc}.failed`), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  }, [onChanged, t]);

  const submitPin = async () => {
    if (!/^\d{6}$/.test(pin) || !pinStep?.phoneNumberId) { toast({ title: t(`${kc}.pinInvalid`), variant: 'destructive' }); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/meta/whatsapp/register', {
        phone_number_id: pinStep.phoneNumberId, pin,
      });
      toast({ title: t(`${kc}.connectedToast`) });
      setPinStep(null);
      onChanged?.(data?.connection);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === 'number_attached_elsewhere') {
        setPinStep(null);
        toast({ title: t(`${kc}.pinAttachedElsewhere`), variant: 'destructive' });
      } else {
        toast({ title: t(`${kc}.failed`), variant: 'destructive' });   // keep PIN step for retry
      }
    } finally {
      setPin('');   // consumed — never retained
      setBusy(false);
    }
  };

  const handleConnect = () => {
    if (!cfg?.configured) { toast({ title: t(`${kc}.notConfigured`), variant: 'destructive' }); return; }
    if (flowRef.current) return;      // a flow is already in progress
    setBusy(true);

    // The CODE (FB.login callback) and the WA session (postMessage) arrive on
    // two independent channels in an unknown order. The coordinator waits for
    // BOTH, completes exactly once, and tears down on any terminal path.
    let messageListener = null;
    let timer = null;
    const teardown = () => {
      if (messageListener) window.removeEventListener('message', messageListener);
      if (timer) clearTimeout(timer);
      messageListener = null;
      timer = null;
      flowRef.current = null;
    };

    const flow = createSignupFlow({
      onComplete: ({ code, phoneNumberId, wabaId, event }) => complete(code, phoneNumberId, wabaId, event),
      onCancel: ({ silent }) => { setBusy(false); if (!silent) toast({ title: t(`${kc}.cancelled`) }); },
      onCleanup: teardown,   // runs exactly once before any onComplete / onCancel
    });
    flowRef.current = flow;

    messageListener = (event) => flow.handleMessage(event.origin, event.data);
    window.addEventListener('message', messageListener);
    // A real Coexistence session spans multiple Meta screens, phone entry,
    // eligibility processing, opening the WhatsApp Business App and QR/in-app
    // approval — well beyond a few minutes. The listener stays valid for a
    // generous human onboarding session (15 min) and is torn down deterministically
    // on any terminal Meta event, on component unmount, and before a new attempt,
    // so no stale callback from an earlier attempt can complete a new one.
    timer = setTimeout(() => flow.handleTimeout(), 900000);   // 15 min safety net

    const launch = (FB) => FB.login(
      (resp) => flow.handleAuthResponse(resp),
      { config_id: cfg.config_id, response_type: 'code', override_default_response_type: true, extras: { version: 'v4', setup: {}, featureType: 'whatsapp_business_app_onboarding' } },
    );

    if (window.FB) {
      // Preloaded on mount → invoke synchronously inside the click gesture so
      // the browser preserves user activation and the popup is not suppressed.
      launch(window.FB);
    } else {
      // SDK not ready yet (slow/failed preload) — best-effort load, then launch.
      loadSdk(cfg.app_id, cfg.graph_version || 'v26.0')
        .then((FB) => { setSdkReady(true); setSdkError(false); launch(FB); })
        .catch(() => {
          setSdkError(true);
          flow.dispose();
          setBusy(false);
          toast({ title: t(`${kc}.failed`), variant: 'destructive' });
        });
    }
  };

  const sdkPreparing = !!cfg?.configured && !sdkReady && !sdkError;

  return (
    <div className="space-y-3" data-testid="whatsapp-signup-section">
      {!hideSeparator && <Separator />}
      <div className="flex items-center gap-2">
        <span className="font-medium text-gray-800">{t(`${kc}.title`)}</span>
        {isConnected && (
          <Badge className="bg-green-100 text-green-700 hover:bg-green-100" data-testid="whatsapp-connected-badge">
            <CheckCircle2 className="h-3 w-3 mr-1" />{t(`${kc}.connected`)}
          </Badge>
        )}
      </div>
      <p className="text-sm text-gray-600">{t(`${kc}.subtitle`)}</p>
      {isConnected ? (
        <div className="text-sm text-gray-700" data-testid="whatsapp-asset-list">
          {activeAssets.map((a) => (
            <div key={a.phone_number_id} className="flex justify-between gap-4" data-testid={`whatsapp-asset-${a.phone_number_id}`}>
              <span className="text-gray-500">{t(`${kc}.numberLabel`)}</span>
              <span className="font-medium">{a.display_phone_number || a.verified_name || a.phone_number_id}</span>
            </div>
          ))}
        </div>
      ) : pinStep ? (
        <div className="space-y-3" data-testid="whatsapp-pin-step">
          <div className="space-y-1.5">
            <label htmlFor="wa-2sv-pin" className="text-sm font-medium text-gray-700">{t(`${kc}.pinLabel`)}</label>
            <Input
              id="wa-2sv-pin"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={6}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder={t(`${kc}.pinPlaceholder`)}
              className="max-w-[180px] tracking-[0.4em]"
              data-testid="whatsapp-pin-input"
            />
            <p className="text-xs text-gray-500" data-testid="whatsapp-pin-help">{t(`${kc}.pinHelp`)}</p>
            <p className="text-xs text-gray-400" data-testid="whatsapp-pin-recovery">{t(`${kc}.pinRecovery`)}</p>
          </div>
          <Button onClick={submitPin} disabled={busy || !/^\d{6}$/.test(pin)} className="bg-green-600 hover:bg-green-700" data-testid="whatsapp-pin-submit-btn">
            {busy
              ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t(`${kc}.connecting`)}</>)
              : t(`${kc}.pinSubmit`)}
          </Button>
        </div>
      ) : (
        <Button onClick={handleConnect} disabled={busy || sdkPreparing} className="bg-green-600 hover:bg-green-700" data-testid="whatsapp-connect-btn">
          {busy
            ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t(`${kc}.connecting`)}</>)
            : sdkPreparing
              ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t(`${kc}.preparing`)}</>)
              : (<><Link2 className="h-4 w-4 mr-2" />{!isConnected && provisioningAssets.length > 0 ? t(`${kc}.retryConnect`) : t(`${kc}.connectBtn`)}</>)}
        </Button>
      )}
      {!isConnected && !pinStep && provisioningAssets.length > 0 && (
        <div className="mt-2 flex items-start gap-2 text-sm text-amber-700" data-testid="whatsapp-provisioning-state">
          <Loader2 className="h-4 w-4 mt-0.5 animate-spin shrink-0" />
          <span>{t(`${kc}.actionRequired`)}</span>
        </div>
      )}
    </div>
  );
};

export const MetaConnectionCard = ({ hideLeadAds = false }) => {
  const { t } = useTranslation();
  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('main'); // 'main' | 'pages' | 'forms'
  const [starting, setStarting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pages, setPages] = useState([]);
  const [forms, setForms] = useState([]);
  const [pickPage, setPickPage] = useState(null);
  const [pickForms, setPickForms] = useState([]);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/meta/connection/status');
      setConn(data?.connection || null);
    } catch {
      setConn(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('meta');
    if (flag === 'connected') toast({ title: t('provider.metaConnect.statusConnected') });
    else if (flag === 'error') toast({ title: t('provider.metaConnect.errorState'), variant: 'destructive' });
    if (flag) {
      params.delete('meta');
      const qs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // MID-02 / GAP-01 — `conn.configured` comes from
  // `MetaConnectionService.safe_status()` → `is_configured()`, i.e. the
  // GENERIC Lead Ads / Messenger Meta app contract. It gates Lead Ads UI
  // ONLY. WhatsApp has its own dedicated contract
  // (`GET /api/meta/whatsapp/config` → `whatsapp_is_configured()`), which
  // `WhatsAppSignupSection` already fetches and enforces itself, so the
  // WhatsApp section must never be hidden by this value.
  const isLeadAdsConfigured = conn?.configured !== false;
  const isConnected = !!conn?.connected;
  const isError = conn?.status === 'reauthorization_required';
  const selectedPage = conn?.selected_page || null;
  const selectedForms = conn?.selected_forms || [];
  const hasPage = !!selectedPage?.page_id;
  const hasForms = selectedForms.length > 0;
  const leadDelivered = !!conn?.lead_delivery?.subscribed;
  const messenger = conn?.messenger || {};

  const handleConnect = async () => {
    setStarting(true);
    try {
      const { data } = await api.post('/meta/oauth/start');
      if (data?.authorization_url) window.location.assign(data.authorization_url);
      else throw new Error('no_url');
    } catch (e) {
      toast({ title: t(`provider.metaConnect.${errorKey(e)}`), variant: 'destructive' });
      setStarting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await api.post('/meta/connection/disconnect');
      toast({ title: t('provider.metaConnect.disconnected') });
      await loadStatus();
      setView('main');
    } catch (e) {
      toast({ title: t(`provider.metaConnect.${errorKey(e)}`), variant: 'destructive' });
    } finally {
      setDisconnecting(false);
    }
  };

  const openPages = async () => {
    setBusy(true);
    try {
      const { data } = await api.get('/meta/pages');
      setPages(data?.pages || []);
      setPickPage(selectedPage?.page_id || null);
      setView('pages');
    } catch (e) {
      toast({ title: t(`provider.metaConnect.${errorKey(e)}`), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const savePage = async () => {
    if (!pickPage) return;
    setBusy(true);
    try {
      const { data } = await api.post('/meta/page/select', { page_id: pickPage });
      setConn(data?.connection || conn);
      toast({ title: t('provider.metaConnect.saved') });
      setView('main');
    } catch (e) {
      toast({ title: t(`provider.metaConnect.${errorKey(e)}`), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const openForms = async () => {
    setBusy(true);
    try {
      const { data } = await api.get('/meta/forms');
      setForms(data?.forms || []);
      setPickForms(selectedForms.map((f) => f.form_id));
      setView('forms');
    } catch (e) {
      toast({ title: t(`provider.metaConnect.${errorKey(e)}`), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const toggleForm = (id) =>
    setPickForms((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const saveForms = async () => {
    if (pickForms.length === 0) {
      toast({ title: t('provider.metaConnect.selectAtLeastOneForm'), variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/meta/forms/select', { form_ids: pickForms });
      setConn(data?.connection || conn);
      toast({ title: t('provider.metaConnect.saved') });
      setView('main');
    } catch (e) {
      toast({ title: t(`provider.metaConnect.${errorKey(e)}`), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const handleSubscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/meta/page/subscribe');
      setConn(data?.connection || conn);
      toast({ title: t('provider.metaConnect.leadDeliveryConfigured') });
    } catch (e) {
      toast({ title: t(`provider.metaConnect.${errorKey(e)}`), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const handleEnableMessenger = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/meta/messenger/subscribe');
      setConn(data?.connection || conn);
      toast({ title: t('provider.metaConnect.messengerEnabled') });
    } catch (e) {
      toast({ title: t(`provider.metaConnect.${errorKey(e)}`), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const kc = 'provider.metaConnect';

  // Owner UI decision (WhatsApp UAT surface): on the Provider Lead Distribution
  // screen, hide the Meta Lead Ads card/heading/copy/Connect-Meta button and
  // expose ONLY the WhatsApp connection section. All Lead Ads logic, handlers,
  // routes, OAuth and backend capability remain intact and reusable elsewhere.
  if (hideLeadAds) {
    return (
      <Card className="border-green-200" data-testid="meta-connection-card">
        <CardContent className="pt-6 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="meta-status-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> …
            </div>
          )}
          {/* MID-02 / GAP-01 — this surface deliberately hides Lead Ads, so
              the GENERIC Lead Ads `configured` alert does not belong here and
              must not gate the WhatsApp section. WhatsApp readiness is owned
              by `WhatsAppSignupSection` via `GET /api/meta/whatsapp/config`. */}
          {!loading && (
            <WhatsAppSignupSection conn={conn} onChanged={(c) => (c ? setConn(c) : loadStatus())} hideSeparator />
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-blue-200" data-testid="meta-connection-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600 text-white">
            <Megaphone className="h-4 w-4" />
          </span>
          {t(`${kc}.title`)}
          {isConnected && (
            <Badge className="ml-1 bg-green-100 text-green-700 hover:bg-green-100" data-testid="meta-connected-badge">
              <CheckCircle2 className="h-3 w-3 mr-1" />{t(`${kc}.connected`)}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>{t(`${kc}.subtitle`)}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="meta-status-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> …
          </div>
        )}

        {!loading && !isLeadAdsConfigured && (
          <Alert data-testid="meta-not-configured-alert">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{t(`${kc}.notConfigured`)}</AlertDescription>
          </Alert>
        )}

        {/* ── A. Not connected ── */}
        {!loading && isLeadAdsConfigured && !isConnected && (
          <>
            {isError && (
              <Alert variant="destructive" data-testid="meta-error-alert">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{t(`${kc}.errorState`)}</AlertDescription>
              </Alert>
            )}
            <Button onClick={handleConnect} disabled={starting} className="bg-blue-600 hover:bg-blue-700" data-testid="meta-connect-btn">
              {starting
                ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t(`${kc}.connecting`)}</>)
                : (<><Link2 className="h-4 w-4 mr-2" />{isError ? t(`${kc}.reconnect`) : t(`${kc}.connectBtn`)}</>)}
            </Button>
          </>
        )}

        {/* ── Connected: MAIN view (setup summary + actions) ── */}
        {!loading && isLeadAdsConfigured && isConnected && view === 'main' && (
          <div className="space-y-4" data-testid="meta-connected-view">
            {isError && (
              <Alert variant="destructive" data-testid="meta-error-alert">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{t(`${kc}.reauthRequired`)}</AlertDescription>
              </Alert>
            )}

            {/* B. no page selected */}
            {!hasPage && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">{t(`${kc}.setupIncompletePage`)}</p>
                <Button onClick={openPages} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="meta-select-page-btn">
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LayoutGrid className="h-4 w-4 mr-2" />}
                  {t(`${kc}.selectPage`)}
                </Button>
              </div>
            )}

            {/* C / D. page selected */}
            {hasPage && (
              <div className="space-y-3 text-sm" data-testid="meta-config-summary">
                <div className="flex justify-between gap-4">
                  <span className="text-gray-500">{t(`${kc}.summaryPage`)}</span>
                  <span className="font-medium text-gray-900" data-testid="meta-summary-page">{selectedPage.page_name || selectedPage.page_id}</span>
                </div>
                <div className="flex justify-between gap-4 items-start">
                  <span className="text-gray-500">{t(`${kc}.summaryForms`)}</span>
                  <span className="font-medium text-gray-900 text-right" data-testid="meta-summary-forms">
                    {hasForms ? selectedForms.map((f) => f.form_name || f.form_id).join(', ') : '—'}
                  </span>
                </div>

                <Separator />

                {hasForms ? (
                  <div className="space-y-2" data-testid="meta-source-ready">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle2 className="h-4 w-4" />
                      <span className="font-medium">
                        {leadDelivered ? t(`${kc}.leadDeliveryConfigured`) : t(`${kc}.sourceConfigured`)}
                      </span>
                    </div>
                    {!leadDelivered && (
                      <p className="text-xs text-gray-500">{t(`${kc}.leadDeliveryHint`)}</p>
                    )}
                    <div className="flex items-center gap-2 pt-1" data-testid="meta-messenger-row">
                      <span className="text-sm text-gray-600">{t(`${kc}.messengerLabel`)}:</span>
                      {messenger.enabled ? (
                        <span className="text-sm font-medium text-green-700" data-testid="meta-messenger-enabled">
                          {t(`${kc}.messengerEnabled`)}
                        </span>
                      ) : messenger.permission_granted ? (
                        <Button size="sm" variant="outline" onClick={handleEnableMessenger} disabled={busy} data-testid="meta-messenger-enable-btn">
                          {t(`${kc}.messengerEnable`)}
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={handleConnect} disabled={busy} data-testid="meta-messenger-reauth-btn">
                          {t(`${kc}.messengerReauthorize`)}
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-amber-700" data-testid="meta-forms-pending">{t(`${kc}.setupIncompleteForms`)}</p>
                )}

                <div className="flex flex-wrap gap-2 pt-1">
                  {hasForms && !leadDelivered && (
                    <Button onClick={handleSubscribe} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="meta-configure-delivery-btn">
                      {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Megaphone className="h-4 w-4 mr-2" />}
                      {t(`${kc}.configureLeadDelivery`)}
                    </Button>
                  )}
                  {!hasForms && (
                    <Button onClick={openForms} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="meta-select-forms-btn">
                      {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                      {t(`${kc}.selectForms`)}
                    </Button>
                  )}
                  {hasForms && (
                    <Button variant="outline" onClick={openForms} disabled={busy} data-testid="meta-change-forms-btn">
                      <FileText className="h-4 w-4 mr-2" />{t(`${kc}.changeForms`)}
                    </Button>
                  )}
                  <Button variant="outline" onClick={openPages} disabled={busy} data-testid="meta-change-page-btn">
                    <LayoutGrid className="h-4 w-4 mr-2" />{t(`${kc}.changePage`)}
                  </Button>
                </div>
              </div>
            )}

            <Separator />
            <div className="flex flex-wrap gap-2">
              <Button variant="ghost" size="sm" onClick={handleDisconnect} disabled={disconnecting}
                className="text-gray-600" data-testid="meta-disconnect-btn">
                {disconnecting
                  ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t(`${kc}.disconnecting`)}</>)
                  : t(`${kc}.disconnect`)}
              </Button>
            </div>
          </div>
        )}

        {/* ── P1: WhatsApp Embedded Signup ──
            MID-02 / GAP-01: visibility depends ONLY on the dedicated WhatsApp
            configuration contract, which `WhatsAppSignupSection` resolves
            itself from `GET /api/meta/whatsapp/config`. It is independent of
            the generic Lead Ads `configured` / `connected` state. */}
        {!loading && view === 'main' && (
          <WhatsAppSignupSection conn={conn} onChanged={(c) => (c ? setConn(c) : loadStatus())} />
        )}

        {/* ── PAGES view ── */}
        {!loading && isConnected && view === 'pages' && (
          <div className="space-y-3" data-testid="meta-pages-view">
            <div className="flex items-center justify-between">
              <p className="font-medium text-gray-800">{t(`${kc}.pagesTitle`)}</p>
              <Button variant="ghost" size="sm" onClick={openPages} disabled={busy} data-testid="meta-refresh-pages-btn">
                <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            {pages.length === 0 ? (
              <p className="text-sm text-gray-500" data-testid="meta-pages-empty">{t(`${kc}.pagesEmpty`)}</p>
            ) : (
              <div className="space-y-1.5 max-h-60 overflow-y-auto">
                {pages.map((p) => (
                  <button
                    key={p.page_id}
                    type="button"
                    onClick={() => setPickPage(p.page_id)}
                    data-testid={`meta-page-option-${p.page_id}`}
                    className={`w-full text-left flex items-center justify-between rounded-lg border px-3 py-2 text-sm transition-colors ${
                      pickPage === p.page_id ? 'border-blue-500 bg-blue-50 text-blue-900' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <span className="font-medium">{p.page_name || p.page_id}</span>
                    {pickPage === p.page_id && <CheckCircle2 className="h-4 w-4 text-blue-600" />}
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => setView('main')} disabled={busy} data-testid="meta-pages-back-btn">
                <ArrowLeft className="h-4 w-4 mr-1" />{t(`${kc}.back`)}
              </Button>
              <Button size="sm" onClick={savePage} disabled={busy || !pickPage} className="bg-blue-600 hover:bg-blue-700" data-testid="meta-save-page-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}{t(`${kc}.save`)}
              </Button>
            </div>
          </div>
        )}

        {/* ── FORMS view ── */}
        {!loading && isConnected && view === 'forms' && (
          <div className="space-y-3" data-testid="meta-forms-view">
            <div className="flex items-center justify-between">
              <p className="font-medium text-gray-800">{t(`${kc}.formsTitle`)}</p>
              <Button variant="ghost" size="sm" onClick={openForms} disabled={busy} data-testid="meta-refresh-forms-btn">
                <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            {forms.length === 0 ? (
              <p className="text-sm text-gray-500" data-testid="meta-forms-empty">{t(`${kc}.noFormsFound`)}</p>
            ) : (
              <div className="space-y-1.5 max-h-60 overflow-y-auto">
                {forms.map((f) => (
                  <label
                    key={f.form_id}
                    data-testid={`meta-form-option-${f.form_id}`}
                    className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer"
                  >
                    <Checkbox
                      checked={pickForms.includes(f.form_id)}
                      onCheckedChange={() => toggleForm(f.form_id)}
                      data-testid={`meta-form-checkbox-${f.form_id}`}
                    />
                    <span className="flex-1 font-medium">{f.form_name || f.form_id}</span>
                    {f.status && (
                      <Badge variant="secondary" className="text-[11px] uppercase">{f.status}</Badge>
                    )}
                  </label>
                ))}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={() => setView('main')} disabled={busy} data-testid="meta-forms-back-btn">
                <ArrowLeft className="h-4 w-4 mr-1" />{t(`${kc}.back`)}
              </Button>
              <Button size="sm" onClick={saveForms} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="meta-save-forms-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}{t(`${kc}.save`)}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MetaConnectionCard;
